import type {
  AnalysisPostFormulaParameter,
  Measure as ApiMeasure,
  AutomaticMeasure as ApiAutomaticMeasure,
  AutomaticMeasureFollowUp as ApiAutomaticMeasureFollowUp,
  AnalysisPostCycleCandidate as ApiAnalysisPostCycleCandidate,
} from '../services/api/generated/model';
import { CheckerType } from './measure-checker.interface';
import { FormulaParameter } from './formula.interface';

export type Measure = Omit<ApiMeasure, 'description' | 'priority' | 'suspended'> & {
  description: string;
  priority?: boolean;
  // Automatic measures carry no suspension flag; keep it optional so every measure
  // kind stays interchangeable in pooled (referenceable) lists.
  suspended?: boolean;
};

export type GenericMeasure = Pick<Measure, 'id' | 'name' | 'assetId' | 'barcode' | 'description' | 'type'>;

export type ManualMeasure = Measure;

export interface DetailedMeasureFields {
  measureTypeName?: string | null;
  unitName?: string | null;
  unitSymbol?: string | null;
  localisationName?: string | null;
  sortNr?: number;
}

export type DetailedManualMeasure = ManualMeasure & DetailedMeasureFields;

export type DetailedAutomaticMeasure = AutomaticMeasure & DetailedMeasureFields;

export type DetailedAnalysisPost = AnalysisPost & DetailedMeasureFields;

export interface FrequencyValidationError {
  message: string;
  condition: (value: number | null) => boolean;
}

export enum AutomaticFrequency {
  TenMinutes = 1,
  FifteenMinutes = 2,
  OneHour = 3,
  FourHours = 4,
  SixHours = 5,
  TwelveHours = 6,
  OneDay = 7,
  OneWeek = 8,
  OneMonth = 9,
  FourMonths = 10,
  SixMonths = 11,
  OneYear = 12,
}

export type AutomaticMeasureFrequencies = AutomaticFrequency;

export interface FrequencyOption {
  /** Transloco key, NOT a literal label. */
  name: string;
  id: AutomaticFrequency;
}

export const FREQUENCY_OPTIONS: FrequencyOption[] = [
  { name: 'entities.automaticMeasure.frequencies.tenMinutes', id: AutomaticFrequency.TenMinutes },
  { name: 'entities.automaticMeasure.frequencies.fifteenMinutes', id: AutomaticFrequency.FifteenMinutes },
  { name: 'entities.automaticMeasure.frequencies.oneHour', id: AutomaticFrequency.OneHour },
  { name: 'entities.automaticMeasure.frequencies.fourHours', id: AutomaticFrequency.FourHours },
  { name: 'entities.automaticMeasure.frequencies.sixHours', id: AutomaticFrequency.SixHours },
  { name: 'entities.automaticMeasure.frequencies.twelveHours', id: AutomaticFrequency.TwelveHours },
  { name: 'entities.automaticMeasure.frequencies.oneDay', id: AutomaticFrequency.OneDay },
  { name: 'entities.automaticMeasure.frequencies.oneWeek', id: AutomaticFrequency.OneWeek },
  { name: 'entities.automaticMeasure.frequencies.oneMonth', id: AutomaticFrequency.OneMonth },
  { name: 'entities.automaticMeasure.frequencies.fourMonths', id: AutomaticFrequency.FourMonths },
  { name: 'entities.automaticMeasure.frequencies.sixMonths', id: AutomaticFrequency.SixMonths },
  { name: 'entities.automaticMeasure.frequencies.oneYear', id: AutomaticFrequency.OneYear },
];

export type AutomaticMeasure = Omit<ApiAutomaticMeasure, 'description' | 'priority' | 'frequency' | 'idMeasure'> & {
  description: string;
  frequency: AutomaticMeasureFrequencies;
  priority?: boolean;
};

export type AutomaticMeasureFollow = Omit<
  ApiAutomaticMeasureFollowUp,
  'locationsIds' | 'lastValueDate' | 'id' | 'assetId' | 'measureTypeId'
> & {
  id: number;
  name: string;
  assetId: number;
  measureTypeId: number;
  locationsIds: number[] | string | null;
  lastValueDate: Date | string | null;
  measureTypeName?: string;
  localisationName?: string;
};

export enum MeasureValueStatus {
  Empty = 0,
  Error = 1,
  OutOfBounds = 2,
  Unchecked = 3,
  Valid = 4,
  Pending = 5,
}

export interface AutomaticMeasureValue {
  id: number | null;
  timestamp: Date;
  inputValue: number | null;
  value: number | null;
  valueMinChecker?: number | null;
  valueMaxChecker?: number | null;
  status: MeasureValueStatus;
}

export interface RawAutomaticMeasureValue {
  id: number | null;
  timestamp: string;
  inputValue?: number | null;
  value?: number | null;
  valueMinChecker?: number | null;
  valueMaxChecker?: number | null;
  status: MeasureValueStatus;
}

export type AnalysisPost = Measure & {
  locationIds?: number[];
  cycleSafe?: boolean;
  // Carried by GET /analysis-posts/{id} (AnalysisPostDetail); absent from list rows.
  formulaParameters?: AnalysisPostFormulaParameter[];
};

export type ReferenceableMeasure = Measure & { cycleSafe?: boolean };

export type CycleSafeCandidate = ApiAnalysisPostCycleCandidate;

export interface CreateAnalysisPostPayload {
  name: string;
  description: string;
  assetId: number;
  unitId: number;
  locationIds: number[];
  formulaStartTs: Date | string;
  formulaParameters: FormulaParameter[];
  formulaExpression: string;
  verificationType?: CheckerType;
  verificationStartTs?: Date | string;
  negativeGap?: number;
  positiveGap?: number;
  formulaFirstParameters?: FormulaParameter[];
  formulaFirstExpression?: string;
  formulaSecondParameters?: FormulaParameter[];
  formulaSecondExpression?: string;
}
